import { useMemo, useState, useEffect, useCallback } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Clock, Search, X } from "lucide-react";
import i18n from "i18next";
import { useTranslation } from "@/packages/utils";
import { springs } from "../motion";
import { ActivityItem } from "./ActivityItem";
import { ActivityFilters } from "./ActivityFilters";
import { Button } from "../primitives";
import type { ActivityEvent, ActivityFeedProps } from "./types";

// ─── Date Grouping ───────────────────────────────────────────────────────────

function getDateGroupLabel(date: Date): string {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterday = new Date(today.getTime() - 86_400_000);
  const d = new Date(date.getFullYear(), date.getMonth(), date.getDate());

  if (d.getTime() === today.getTime()) return i18n.t("common:time.today");
  if (d.getTime() === yesterday.getTime())
    return i18n.t("common:time.yesterday");
  return date.toLocaleDateString(i18n.language, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function groupByDate(
  items: ActivityEvent[],
): Array<{ label: string; events: ActivityEvent[] }> {
  const groups = new Map<string, ActivityEvent[]>();
  for (const item of items) {
    const key = getDateGroupLabel(item.timestamp);
    const existing = groups.get(key);
    if (existing) {
      existing.push(item);
    } else {
      groups.set(key, [item]);
    }
  }
  return Array.from(groups.entries()).map(([label, events]) => ({
    label,
    events,
  }));
}

// ─── Date Separator ─────────────────────────────────────────────────────────

function DateSeparator({ label }: { label: string }) {
  return (
    <div className="flex items-center gap-3 py-2 mb-1">
      <div className="flex-1 h-px bg-border/40" />
      <span className="text-[10px] font-semibold text-muted uppercase tracking-widest shrink-0">
        {label}
      </span>
      <div className="flex-1 h-px bg-border/40" />
    </div>
  );
}

// ─── Empty State ─────────────────────────────────────────────────────────────

function EmptyState({
  title,
  description,
}: {
  title: string;
  description: string;
}) {
  return (
    <motion.div
      className="flex-1 flex items-center justify-center py-16"
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -4 }}
      transition={springs.smooth}
    >
      <div className="flex flex-col items-center gap-3 text-center">
        <motion.div
          className="h-12 w-12 rounded-full bg-card flex items-center justify-center"
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={springs.bouncy}
        >
          <Clock size={24} className="text-muted" strokeWidth={1.5} />
        </motion.div>
        <p className="text-sm font-medium text-primary">{title}</p>
        <p className="text-xs text-muted max-w-[240px]">{description}</p>
      </div>
    </motion.div>
  );
}

// ─── Helper: Flatten metadata for search ───────────────────────────────────

function getSearchableText(item: ActivityEvent): string {
  const parts: string[] = [item.title, item.description ?? ""];

  // Add actor name
  if (item.actor?.name) {
    parts.push(item.actor.name);
  }

  // Add related entity name
  if (item.relatedEntity?.name) {
    parts.push(item.relatedEntity.name);
  }

  // Flatten metadata values
  if (item.metadata) {
    const metadataValues = Object.values(item.metadata)
      .filter((v): v is string => typeof v === "string")
      .join(" ");
    if (metadataValues) {
      parts.push(metadataValues);
    }
  }

  // Add changes (from/to values)
  if (item.changes) {
    const changesValues = Object.values(item.changes)
      .map((c) => `${c.from} ${c.to}`)
      .join(" ");
    if (changesValues) {
      parts.push(changesValues);
    }
  }

  return parts.join(" ").toLowerCase();
}

// ─── ActivityFeed ─────────────────────────────────────────────────────────────

export function ActivityFeed({
  items,
  emptyTitle,
  emptyDescription,
  persistFiltersToUrl = false,
  urlSearchParams,
  onUrlUpdate,
  eventTypeRenderers,
  fieldLabels,
}: ActivityFeedProps) {
  const { t } = useTranslation(["common"]);
  const resolvedEmptyTitle = emptyTitle ?? t("common:labels.noActivityYet");
  const resolvedEmptyDescription =
    emptyDescription ?? t("common:labels.activityDescription");

  // Get values from URL params if persistence is enabled
  const urlCategories = persistFiltersToUrl
    ? urlSearchParams?.categories
    : undefined;
  const urlSearch = persistFiltersToUrl ? urlSearchParams?.search : undefined;

  // Initialize state: from URL if available, otherwise default to ["all"]
  const [activeFilters, setActiveFilters] = useState<string[]>(() => {
    if (urlCategories) {
      const parsed = urlCategories.split(",").filter(Boolean);
      return parsed.length > 0 ? parsed : ["all"];
    }
    return ["all"];
  });

  // Search state
  const [searchQuery, setSearchQuery] = useState<string>(() => {
    if (urlSearch) {
      return urlSearch;
    }
    return "";
  });

  // Debounced search for URL sync
  const [debouncedSearch, setDebouncedSearch] = useState(searchQuery);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Sync state to URL when it changes
  useEffect(() => {
    if (persistFiltersToUrl && onUrlUpdate) {
      const categoriesParam =
        activeFilters.length === 0 || activeFilters.includes("all")
          ? undefined
          : activeFilters.join(",");
      const searchParam = debouncedSearch || undefined;
      onUrlUpdate({
        categories: categoriesParam,
        search: searchParam,
      });
    }
  }, [activeFilters, onUrlUpdate, persistFiltersToUrl, debouncedSearch]);

  // Clear search
  const handleClearSearch = useCallback(() => {
    setSearchQuery("");
  }, []);

  // Filter items by category AND search query
  const filteredItems = useMemo(() => {
    let result = items;

    // Apply category filter
    if (!activeFilters.includes("all")) {
      result = result.filter((item) => activeFilters.includes(item.category));
    }

    // Apply search filter
    const searchLower = debouncedSearch.toLowerCase().trim();
    if (searchLower) {
      result = result.filter((item) =>
        getSearchableText(item).includes(searchLower),
      );
    }

    return result;
  }, [items, activeFilters, debouncedSearch]);

  const groups = useMemo(() => groupByDate(filteredItems), [filteredItems]);

  // Handle filter change (toggle for multi-select)
  const handleFilterChange = useCallback((filterId: string) => {
    setActiveFilters((prev) => {
      // If clicking "all", reset to just ["all"]
      if (filterId === "all") {
        return ["all"];
      }

      // Toggle the filter
      const newFilters = prev.includes(filterId)
        ? prev.filter((f) => f !== filterId)
        : [...prev, filterId];

      // Remove "all" if other filters are selected
      return newFilters.includes("all")
        ? newFilters.filter((f) => f !== "all")
        : newFilters;
    });
  }, []);

  // Global index across all groups for staggered animation
  let globalIndex = 0;

  return (
    <div className="flex flex-col flex-1 min-h-0">
      {/* Search bar */}
      <div className="shrink-0 pb-3">
        <div className="relative mb-3">
          <Search
            className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted"
            strokeWidth={1.8}
          />
          <input
            type="text"
            placeholder={
              t("common:labels.searchActivities") || "Search activities..."
            }
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full h-9 pl-9 pr-8 rounded-button border bg-surface text-[14px] text-primary placeholder:text-muted outline-none focus:border-accent focus:ring-2 focus:ring-accent/10 transition-colors"
          />
          {searchQuery && (
            <Button
              variant="ghost"
              size="sm"
              className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7 p-0"
              onClick={handleClearSearch}
            >
              <X size={14} className="text-muted" />
            </Button>
          )}
        </div>

        {/* Filter chips */}
        <ActivityFilters
          items={items}
          activeFilters={activeFilters}
          onFilterChange={handleFilterChange}
          isMultiSelect={true}
        />
      </div>

      {/* Feed area */}
      <div className="flex-1 overflow-y-auto min-h-0">
        <AnimatePresence mode="wait">
          {filteredItems.length === 0 ? (
            <EmptyState
              key="empty"
              title={
                searchQuery
                  ? t("common:labels.noResults") || "No results found"
                  : resolvedEmptyTitle
              }
              description={
                searchQuery
                  ? t("common:labels.noResultsDescription") ||
                    "Try adjusting your search or filters"
                  : resolvedEmptyDescription
              }
            />
          ) : (
            <motion.div
              key={`feed-${activeFilters.join(",")}-${debouncedSearch}`}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.15 }}
              className="py-2"
            >
              {groups.map((group) => (
                <div key={group.label} className="mb-4">
                  <DateSeparator label={group.label} />
                  <div className="mt-3">
                    {group.events.map((event, i) => {
                      const idx = globalIndex++;
                      return (
                        <ActivityItem
                          key={event.id}
                          event={event}
                          index={idx}
                          isLast={i === group.events.length - 1}
                          renderer={eventTypeRenderers?.[event.type]}
                          fieldLabels={fieldLabels}
                        />
                      );
                    })}
                  </div>
                </div>
              ))}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
